import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeTable } from '../lib/realtime';
import type { Driver } from '../lib/types';

interface UseDriversOptions {
  search?: string;
  status?: Driver['status'] | 'all';
  page?: number;
  perPage?: number;
  sortField?: keyof Driver;
  sortDirection?: 'asc' | 'desc';
  companyId?: string | null;
}

interface UseDriversResult {
  drivers: Driver[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDrivers(options: UseDriversOptions = {}): UseDriversResult {
  const { search = '', status = 'all', page = 1, perPage = 10, sortField = 'full_name', sortDirection = 'asc', companyId = null } = options;
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    let query = supabase
      .from('drivers')
      .select('*, vehicle:vehicles(*)', { count: 'exact' });

    if (status !== 'all') query = query.eq('status', status);
    if (companyId) query = query.eq('company_id', companyId);
    if (search) query = query.ilike('full_name', `%${search}%`);

    query = query
      .order(sortField as string, { ascending: sortDirection === 'asc' })
      .range((page - 1) * perPage, page * perPage - 1);

    const { data, count, error: err } = await query;

    if (err) {
      setError(err.message);
      setDrivers([]);
    } else {
      setDrivers((data as Driver[]) ?? []);
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [search, status, page, perPage, sortField, sortDirection, companyId]);

  useEffect(() => {
    refetch();
    const unsub = subscribeTable('drivers', refetch);
    return unsub;
  }, [refetch]);

  return { drivers, total, loading, error, refetch };
}
